import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiCreditCard, FiPlus, FiEdit, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { meusCartoesAPI, clienteSobreAPI } from '../services/api';
import Layout from '../components/Layout';
import CreditCardBrandIcon from '../components/CreditCardBrandIcon';
import theme from '../styles/theme';

const Container = styled.div`
  padding: ${theme.spacing[6]};
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[8]};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  margin: 0;
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${props => props.variant === 'danger' ? theme.colors.error[500] : props.variant === 'secondary' ? 'transparent' : theme.colors.primary[500]};
  border: 1px solid ${props => props.variant === 'danger' ? theme.colors.error[500] : props.variant === 'secondary' ? theme.colors.border.primary : theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${props => props.variant === 'secondary' ? theme.colors.text.primary : theme.colors.white};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'danger' ? theme.colors.error[600] : props.variant === 'secondary' ? theme.colors.background.primary : theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]};
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.border.primary};
  border-top: 3px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.error[500]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const SuccessMessage = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.success[500]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]};
  color: ${theme.colors.text.secondary};
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing[4]};
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.secondary};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CardItem = styled.div`
  background: ${theme.colors.background.card};
  border: 1px solid ${props => props.$padrao ? theme.colors.primary[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  box-shadow: ${theme.shadows.md};
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    box-shadow: ${theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing[4]};
`;

const CardInfo = styled.div`
  flex: 1;
`;

const CardNumber = styled.div`
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing[2]};
  font-family: 'Courier New', monospace;
  letter-spacing: 2px;
`;

const CardHolder = styled.div`
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing[1]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const CardDetails = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  margin-top: ${theme.spacing[4]};
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.primary};
  min-height: 180px; /* Altura mínima fixa para manter consistência */
  display: flex;
  flex-direction: column;
`;

const CardDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 28px; /* Altura fixa para cada linha */
  margin-bottom: ${theme.spacing[2]};
  
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};
  min-height: 40px; /* Altura fixa para os botões */
  align-items: center;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${props => {
    if (props.$variant === 'primary') return `${theme.colors.primary[500]}20`;
    if (props.$variant === 'success') return `${theme.colors.success[500]}20`;
    return `${theme.colors.text.secondary}20`;
  }};
  color: ${props => {
    if (props.$variant === 'primary') return theme.colors.primary[500];
    if (props.$variant === 'success') return theme.colors.success[500];
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
`;


const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${props => props.variant === 'danger' ? theme.colors.error[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${props => props.variant === 'danger' ? theme.colors.error[500] : theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[1]};

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'danger' ? `${theme.colors.error[500]}20` : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[6]};
`;

const Label = styled.label`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const MeusCartoes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cartoes, setCartoes] = useState([]);
  const [cartoesFiltrados, setCartoesFiltrados] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState('');
  const [loadingClientes, setLoadingClientes] = useState(false);

  useEffect(() => {
    carregarClientes();
  }, []);

  useEffect(() => {
    if (clientes.length > 0) {
      carregarCartoes();
    }
  }, [clientes]);

  useEffect(() => {
    if (clienteSelecionado && clienteSelecionado !== '') {
      const clienteIdNum = parseInt(clienteSelecionado, 10);
      
      if (isNaN(clienteIdNum)) {
        console.error('[ERROR] clienteSelecionado não é um número válido:', clienteSelecionado);
        setCartoesFiltrados(cartoes);
        return;
      }
      
      const filtrados = cartoes.filter(cartao => {
        // Tentar diferentes nomes de campo possíveis (cliente_id ou clienteId)
        const rawClienteId = cartao.cliente_id ?? cartao.clienteId;
        
        if (rawClienteId == null) {
          return false;
        }
        
        // Converter para número inteiro
        const cartaoClienteId = parseInt(String(rawClienteId), 10);
        
        if (isNaN(cartaoClienteId)) {
          return false;
        }
        
        return cartaoClienteId === clienteIdNum;
      });
      
      setCartoesFiltrados(filtrados);
    } else {
      setCartoesFiltrados(cartoes);
    }
  }, [clienteSelecionado, cartoes]);

  const carregarClientes = async () => {
    try {
      setLoadingClientes(true);
      const response = await clienteSobreAPI.listarClientes();
      if (response.data?.success) {
        const clientesLista = response.data.data || [];
        setClientes(clientesLista);
        // Se houver apenas um cliente, selecionar automaticamente
        if (clientesLista.length === 1) {
          setClienteSelecionado(String(clientesLista[0].id));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar clientes', error);
    } finally {
      setLoadingClientes(false);
    }
  };

  const carregarCartoes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await meusCartoesAPI.listar();
      
      if (response.data.success) {
        const cartoesCarregados = response.data.data || [];
        
        setCartoes(cartoesCarregados);
        // Aplicar filtro se houver cliente selecionado
        if (clienteSelecionado && clienteSelecionado !== '') {
          const clienteIdNum = parseInt(clienteSelecionado, 10);
          
          if (isNaN(clienteIdNum)) {
            console.error('[ERROR] clienteSelecionado não é um número válido:', clienteSelecionado);
            setCartoesFiltrados(cartoesCarregados);
            return;
          }
          
          const filtrados = cartoesCarregados.filter(cartao => {
            // Tentar diferentes nomes de campo possíveis (cliente_id ou clienteId)
            const rawClienteId = cartao.cliente_id ?? cartao.clienteId;
            
            if (rawClienteId == null) {
              return false;
            }
            
            // Converter para número inteiro
            const cartaoClienteId = parseInt(String(rawClienteId), 10);
            
            if (isNaN(cartaoClienteId)) {
              return false;
            }
            
            const match = cartaoClienteId === clienteIdNum;
            
            return match;
          });
          
          setCartoesFiltrados(filtrados);
        } else {
          setCartoesFiltrados(cartoesCarregados);
        }
      } else {
        setError(response.data.message || 'Erro ao carregar cartões');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar cartões');
    } finally {
      setLoading(false);
    }
  };

  // Função handleDeletar removida - não é permitido excluir cartões cadastrados

  const formatarBandeira = (bandeira) => {
    if (!bandeira) return 'Não informado';
    return bandeira.toUpperCase();
  };

  const formatarNumero = (numero) => {
    if (!numero) return '****';
    return `**** **** **** ${numero}`;
  };

  const formatarData = (mes, ano) => {
    if (!mes || !ano) return 'N/A';
    return `${mes.padStart(2, '0')}/${ano}`;
  };

  const formatarTelefone = (telefone) => {
    if (!telefone) return 'Não informado';
    const apenasNumeros = telefone.replace(/\D/g, '');
    
    // Formatar como (00) 00000-0000 para celular ou (00) 0000-0000 para fixo
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (apenasNumeros.length === 10) {
      return apenasNumeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    
    return telefone;
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <LoadingContainer>
            <LoadingSpinner />
            <p>Carregando cartões...</p>
          </LoadingContainer>
        </Container>
      </Layout>
    );
  }

  const mostrarFiltroCliente = clientes.length > 1;

  return (
    <Layout>
      <Container>
        <Header>
          <Title>Meus Cartões</Title>
          <Button onClick={() => navigate('/meus-cartoes/novo')}>
            <FiPlus />
            Adicionar Cartão
          </Button>
        </Header>

        {mostrarFiltroCliente && (
          <FormGroup>
            <Label>Cliente</Label>
            <Select
              value={clienteSelecionado}
              onChange={(e) => setClienteSelecionado(e.target.value)}
            >
              <option value="">Todos os clientes</option>
              {clientes.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nome_fantasia || cliente.nome_social || `Cliente #${cliente.id}`}
                </option>
              ))}
            </Select>
          </FormGroup>
        )}

        {error && (
          <ErrorMessage>
            <FiAlertCircle />
            {error}
          </ErrorMessage>
        )}

        {success && (
          <SuccessMessage>
            <FiCheckCircle />
            {success}
          </SuccessMessage>
        )}

        {cartoesFiltrados.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <FiCreditCard />
            </EmptyIcon>
            <h3>Nenhum cartão cadastrado</h3>
            <p style={{ marginTop: theme.spacing[2], marginBottom: theme.spacing[6] }}>
              Adicione um cartão de crédito para facilitar seus pagamentos
            </p>
            <Button onClick={() => navigate('/meus-cartoes/novo')}>
              <FiPlus />
              Adicionar Primeiro Cartão
            </Button>
          </EmptyState>
        ) : (
          <CardsGrid>
            {cartoesFiltrados.map((cartao) => (
              <CardItem key={cartao.id} $padrao={cartao.padrao}>
                <CardHeader>
                  <CardInfo>
                    <CardNumber>{formatarNumero(cartao.number_last_four)}</CardNumber>
                    <CardHolder>
                      <span>{cartao.holder_name}</span>
                      <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
                        {cartao.padrao && (
                          <Badge $variant="success">
                            Principal
                          </Badge>
                        )}
                      </div>
                    </CardHolder>
                  </CardInfo>
                </CardHeader>
                <CardDetails>
                  <CardDetailRow>
                    <span>Bandeira:</span>
                    <CreditCardBrandIcon brand={cartao.bandeira} size="48px" />
                  </CardDetailRow>
                  <CardDetailRow>
                    <span>Validade:</span>
                    <span>{formatarData(cartao.expiry_month, cartao.expiry_year)}</span>
                  </CardDetailRow>
                  <CardDetailRow>
                    <span>Email:</span>
                    <span>{cartao.holder_email || 'Não informado'}</span>
                  </CardDetailRow>
                  <CardDetailRow>
                    <span>Telefone:</span>
                    <span>{formatarTelefone(cartao.holder_mobile_phone)}</span>
                  </CardDetailRow>
                </CardDetails>
                <CardActions>
                  <ActionButton onClick={() => navigate(`/meus-cartoes/${cartao.id}/editar`)}>
                    <FiEdit />
                    Editar
                  </ActionButton>
                  {/* Botão de excluir removido - não é permitido excluir cartões cadastrados */}
                </CardActions>
              </CardItem>
            ))}
          </CardsGrid>
        )}
      </Container>
    </Layout>
  );
};

export default MeusCartoes;

